// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Lista de emails de fallback (caso a tabela não exista)
const FALLBACK_ADMIN_EMAILS = [
  'contato@vegarobotics.com.br',
  'admin@vegarobotics.com.br',
  'vega@vegarobotics.com.br',
  'rafaelmdeveloper@gmail.com',
  'vegarobotics.mkt@gmail.com'
];

// Definição de permissões por role
const PERMISSIONS = {
  admin: {
    // Leads
    'leads.view': true,
    'leads.view_all': true,
    'leads.create': true,
    'leads.update_status': true,
    'leads.update_contact': true,
    'leads.delete': true,
    'leads.export': true,
    'leads.assign': true,
    'leads.add_note': true,
    'leads.add_reminder': true,
    'leads.view_full_contact': true,
    // Blog
    'blog.view': true,
    'blog.create': true,
    'blog.update': true,
    'blog.delete': true,
    // Vagas
    'jobs.view': true,
    'jobs.create': true,
    'jobs.update': true,
    'jobs.delete': true,
    'applications.view': true,
    'applications.update': true,
    // Outros
    'analytics.view': true,
    'analytics.view_all': true,
    'settings.view': true,
    'settings.update': true,
    'users.view': true,
    'users.create': true,
    'users.update': true,
    'users.delete': true,
    'market_intelligence.view': true,
    'activity_logs.view': true,
  },
  seller: {
    // Leads - Limitado
    'leads.view': true,
    'leads.view_all': false, // Só vê os próprios
    'leads.create': true,
    'leads.update_status': true,
    'leads.update_contact': false, // NÃO pode editar contato
    'leads.delete': false, // NÃO pode excluir
    'leads.export': false, // NÃO pode exportar
    'leads.assign': false, // NÃO pode reatribuir
    'leads.add_note': true,
    'leads.add_reminder': true,
    'leads.view_full_contact': false, // Vê mascarado
    // Blog
    'blog.view': false,
    'blog.create': false,
    'blog.update': false,
    'blog.delete': false,
    // Vagas
    'jobs.view': false,
    'jobs.create': false,
    'jobs.update': false,
    'jobs.delete': false,
    'applications.view': false,
    'applications.update': false,
    // Outros
    'analytics.view': true,
    'analytics.view_all': false, // Só próprias métricas
    'settings.view': false,
    'settings.update': false,
    'users.view': false,
    'users.create': false,
    'users.update': false,
    'users.delete': false,
    'market_intelligence.view': false,
    'activity_logs.view': false,
  },
  viewer: {
    // Leads - Apenas visualização
    'leads.view': true,
    'leads.view_all': false,
    'leads.create': false,
    'leads.update_status': false,
    'leads.update_contact': false,
    'leads.delete': false,
    'leads.export': false,
    'leads.assign': false,
    'leads.add_note': false,
    'leads.add_reminder': false,
    'leads.view_full_contact': false,
    // Tudo mais desativado
    'blog.view': false,
    'blog.create': false,
    'blog.update': false,
    'blog.delete': false,
    'jobs.view': false,
    'jobs.create': false,
    'jobs.update': false,
    'jobs.delete': false,
    'applications.view': false,
    'applications.update': false,
    'analytics.view': true,
    'analytics.view_all': false,
    'settings.view': false,
    'settings.update': false,
    'users.view': false,
    'users.create': false,
    'users.update': false,
    'users.delete': false,
    'market_intelligence.view': false,
    'activity_logs.view': false,
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  // Buscar perfil do usuário no banco
  const fetchUserProfile = useCallback(async (email) => {
    if (!email) return null;

    const emailLower = email.toLowerCase();

    // Primeiro verificar se é email de fallback (sempre tem acesso admin)
    if (FALLBACK_ADMIN_EMAILS.includes(emailLower)) {
      console.log('Email de fallback detectado:', emailLower);
      return {
        email: emailLower,
        role: 'admin',
        name: 'Admin',
        is_active: true
      };
    }

    // Tentar buscar no banco
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('email', emailLower)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        console.warn('Usuário não encontrado na tabela admin_users:', emailLower);
        return null;
      }

      return data;
    } catch (error) {
      console.warn('Erro ao buscar perfil:', error);
      return null;
    }
  }, []);

  // Atualizar último login
  const updateLastLogin = useCallback(async (email) => {
    if (!email) return;

    try {
      await supabase
        .from('admin_users')
        .update({ last_login: new Date().toISOString() })
        .eq('email', email.toLowerCase());
    } catch (error) {
      console.warn('Erro ao atualizar último login:', error);
    }
  }, []);

  // Registrar atividade
  const logActivity = useCallback(async (action, entityType, entityId = null, entityName = null, details = null) => {
    if (!user || !userProfile) return;

    try {
      await supabase.rpc('log_activity', {
        p_user_email: user.email,
        p_user_name: userProfile.name || user.user_metadata?.full_name || 'Usuário',
        p_user_role: userRole,
        p_action: action,
        p_entity_type: entityType,
        p_entity_id: entityId,
        p_entity_name: entityName,
        p_details: details
      });
    } catch (error) {
      // Silenciar erro se função não existir ainda
      console.warn('Erro ao registrar atividade:', error);
    }
  }, [user, userProfile, userRole]);

  // Verificar permissão
  const hasPermission = useCallback((permission) => {
    if (!userRole) return false;
    const rolePermissions = PERMISSIONS[userRole];
    if (!rolePermissions) return false;
    return rolePermissions[permission] === true;
  }, [userRole]);

  // Verificar múltiplas permissões (retorna true se tiver TODAS)
  const hasAllPermissions = useCallback((permissions) => {
    return permissions.every(p => hasPermission(p));
  }, [hasPermission]);

  // Verificar múltiplas permissões (retorna true se tiver ALGUMA)
  const hasAnyPermission = useCallback((permissions) => {
    return permissions.some(p => hasPermission(p));
  }, [hasPermission]);

  useEffect(() => {
    let isMounted = true;
    let timeoutId;

    // Timeout de segurança - não travar mais que 5 segundos
    const safetyTimeout = setTimeout(() => {
      if (isMounted && loading) {
        console.warn('Auth timeout - forcing loading to false');
        setLoading(false);
      }
    }, 5000);

    // Verificar sessão atual
    const getSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.warn('Erro ao obter sessão:', error.message);
        }

        if (isMounted) {
          setUser(session?.user ?? null);

          if (session?.user) {
            // Buscar perfil completo do usuário
            const profile = await fetchUserProfile(session.user.email);

            if (isMounted) {
              setUserProfile(profile);
              setUserRole(profile?.role || null);
              setIsAdmin(profile?.role === 'admin');

              // Atualizar último login
              if (profile) {
                updateLastLogin(session.user.email);
              }
            }
          } else {
            setUserProfile(null);
            setUserRole(null);
            setIsAdmin(false);
          }
          setLoading(false);
        }
      } catch (error) {
        console.error('Erro ao verificar sessão:', error);
        if (isMounted) {
          setUser(null);
          setUserProfile(null);
          setUserRole(null);
          setIsAdmin(false);
          setLoading(false);
        }
      }
    };

    getSession();

    // Escutar mudanças de autenticação
    let subscription;
    try {
      const { data } = supabase.auth.onAuthStateChange(async (_event, session) => {
        if (isMounted) {
          setUser(session?.user ?? null);

          if (session?.user) {
            const profile = await fetchUserProfile(session.user.email);

            if (isMounted) {
              setUserProfile(profile);
              setUserRole(profile?.role || null);
              setIsAdmin(profile?.role === 'admin');

              if (profile) {
                updateLastLogin(session.user.email);
              }
            }
          } else {
            setUserProfile(null);
            setUserRole(null);
            setIsAdmin(false);
          }

          setLoading(false);
        }
      });
      subscription = data.subscription;
    } catch (error) {
      console.error('Erro ao configurar listener de auth:', error);
      if (isMounted) setLoading(false);
    }

    return () => {
      isMounted = false;
      clearTimeout(safetyTimeout);
      if (subscription) subscription.unsubscribe();
    };
  }, [fetchUserProfile, updateLastLogin]);

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/admin`
      }
    });
    if (error) {
      console.error('Erro ao fazer login:', error.message);
      throw error;
    }
  };

  const signOut = async () => {
    // Registrar logout
    if (user && userProfile) {
      await logActivity('logout', 'session');
    }

    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Erro ao fazer logout:', error.message);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    isAdmin,
    userRole,
    userProfile,
    signInWithGoogle,
    signOut,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    logActivity,
    // Helpers
    isSeller: userRole === 'seller',
    isViewer: userRole === 'viewer',
    canAccessAdmin: userRole && ['admin', 'seller', 'viewer'].includes(userRole),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
